import { useEffect, useState } from 'react';
import { Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { GuardedModal, useDialogs } from '@onvice/portal-ui';

/**
 * Einstellungen-Dialog fuer eine persoenliche Uebersicht-Seite.
 * Umbenennen + Loeschen in einem Modal.
 *
 * Dokument-Navigation (Abhakliste 2.94, 2026-10-01): Dialog-Schutz — Klick daneben, X und
 * Escape fragen nach, sobald der Name geändert ist; Löschen und Zurücksetzen fragen über
 * den Portal-Dialog nach; Fehler erscheinen als Meldung.
 *
 * Konzept: 04 Ressourcen/Softwarearchitektur/Widget-Picker-Konzept.md
 */

interface Props {
  open: boolean;
  pageId: string;
  initialName: string;
  /** true wenn die Seite aus einer Vorlage stammt — Reset-Button wird nur dann gezeigt. */
  hasTemplate: boolean;
  /** Admin-Mode (Joerg 2026-06-08): wenn gesetzt, schreiben Rename/Delete/Reset
   *  gegen /admin/users/:actAsUserId/pages/:id statt /my-pages/:id. */
  actAsUserId?: string;
  onClose: () => void;
  /** Wird aufgerufen wenn Name geaendert wurde — Parent reloaded Tabs + Header. */
  onRenamed: (newName: string) => void;
  /** Wird aufgerufen nach erfolgreichem Loeschen — Parent navigiert weg. */
  onDeleted: () => void;
  /** Wird aufgerufen nach erfolgreichem Reset — Parent reloaded die Seite mit dem Vorlage-Layout. */
  onReset: () => void;
}

export default function PageSettingsDialog({ open, pageId, initialName, hasTemplate, actAsUserId, onClose, onRenamed, onDeleted, onReset }: Props) {
  const baseUrl = actAsUserId
    ? `/portal/admin/users/${actAsUserId}/pages/${pageId}`
    : `/portal/my-pages/${pageId}`;
  const dlg = useDialogs();
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState<'rename' | 'delete' | 'reset' | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setBusy(null);
    }
  }, [open, initialName]);

  const trimmed = name.trim();
  const nameChanged = trimmed.length > 0 && trimmed !== initialName;

  const doRename = async () => {
    if (!nameChanged) return;
    setBusy('rename');
    try {
      const r = await api.patch<{ success: boolean }>(baseUrl, { name: trimmed });
      if (!r.success) throw new Error('Speichern fehlgeschlagen');
      onRenamed(trimmed);
      setName(trimmed);
      onClose();
    } catch (e) {
      await dlg.error('Seite wurde nicht umbenannt', e);
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    const ok = await dlg.confirm({
      title: 'Seite löschen?',
      message: 'Diese Seite wird endgültig gelöscht. Die Widget-Anordnung geht verloren.',
      confirmLabel: 'Endgültig löschen',
      danger: true,
    });
    if (!ok) return;
    setBusy('delete');
    try {
      const r = await api.delete<{ success: boolean }>(baseUrl);
      if (!r.success) throw new Error('Löschen fehlgeschlagen');
      onDeleted();
    } catch (e) {
      await dlg.error('Seite wurde nicht gelöscht', e);
    } finally {
      setBusy(null);
    }
  };

  const doReset = async () => {
    const ok = await dlg.confirm({
      title: 'Auf Vorlage zurücksetzen?',
      message: 'Das Layout wird auf den Stand der ursprünglichen Vorlage gesetzt. Deine Anpassungen gehen verloren.',
      confirmLabel: 'Zurücksetzen',
      danger: true,
    });
    if (!ok) return;
    setBusy('reset');
    try {
      const r = await api.post<{ success: boolean; error?: string }>(`${baseUrl}/reset`, {});
      if (!r.success) throw new Error(r.error || 'Zurücksetzen fehlgeschlagen');
      onReset();
      onClose();
    } catch (e) {
      await dlg.error('Seite wurde nicht zurückgesetzt', e);
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  return (
    <GuardedModal
      open
      title="Seite verwalten"
      label="Seiteneinstellungen"
      dirty={nameChanged}
      busy={busy !== null}
      onClose={onClose}
      size="sm"
      testAnchor="page-settings"
      footer={(requestClose) => (
        <>
          <button
            type="button"
            onClick={requestClose}
            disabled={busy !== null}
            className="px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm min-h-[44px]"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void doRename()}
            disabled={!nameChanged || busy !== null}
            data-action="rename"
            className="px-3 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm disabled:opacity-50 min-h-[44px]"
          >
            {busy === 'rename' ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null}
            Umbenennen
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <label className="block">
          <div className="text-sm font-medium mb-1">Name</div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            className="w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground"
            data-test-anchor="page-name"
          />
        </label>

        {hasTemplate && (
          <div className="pt-4 border-t border-default">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Auf Vorlage zurücksetzen</div>
            <p className="text-xs text-muted-foreground mb-2">
              Setzt das Layout dieser Seite auf den Stand der ursprünglichen Vorlage zurück.
              Deine Anpassungen gehen verloren.
            </p>
            <button
              type="button"
              onClick={() => void doReset()}
              disabled={busy !== null}
              data-action="reset"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm min-h-[44px]"
            >
              {busy === 'reset' ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Auf Vorlage zurücksetzen
            </button>
          </div>
        )}

        <div className="pt-4 border-t border-default">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Gefahrenzone</div>
          <button
            type="button"
            onClick={() => void doDelete()}
            disabled={busy !== null}
            data-action="delete"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-500/40 text-red-500 hover:bg-red-500/10 text-sm min-h-[44px]"
          >
            {busy === 'delete' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Seite löschen
          </button>
        </div>
      </div>
    </GuardedModal>
  );
}
